import { useEffect, useRef } from "react";
import { useNavigation } from "react-router";

import { useRootLoaderData } from "~/root";
import { getCookieValue } from "~/utils/cookies";

import {
  computeIdentityDowngradeReset,
  computeLogoutResetTrigger,
} from "./logout-reset-latch";
import { resetClientSegmentIdentity } from "./reset-client-identity";

/**
 * Clears the Segment identity on sign-out (auth-transition audit V3/V4).
 *
 * The server teardown (logout action) clears the SLAS cookies and `hec_pz`,
 * but `ajs_user_id` is a CLIENT cookie analytics.js owns (364-day expiry) —
 * the server never touches it, and it is the top-priority input to
 * `resolveIdentity`. Left in place, every post-logout request kept resolving
 * to the former customer's hashed-email cuid: client D6 hits carried it, and
 * the next `hec_pz` mint re-attached their traits via a real Profile API
 * lookup. `analytics.reset()` clears the ajs cookies + localStorage.
 *
 * Trigger: the sign-out is a navigation `<Form method="post">` to `/logout`
 * (never a fetcher), so it rides `useNavigation()`. The reset fires at
 * submission START (see ./logout-reset-latch.ts): the logout POST is already
 * in flight and needs no Segment identity, and clearing here means the
 * action's redirect + revalidation GETs go out WITHOUT the ajs cookies — the
 * server rebuilds `hec_pz`/`pzHits` as guest and the landing pageview can't
 * attribute to the former customer. Firing at nav-idle (the original shape)
 * let that whole wave carry the stale identity.
 *
 * Accepted edge: if the logout ACTION itself fails, Segment was reset for a
 * still-authed user. Harmless — root revalidates on the logout submit
 * regardless (isAuthRevalidatingSubmit is ungated on actionStatus), the D6
 * cuid mirrors the root loader's session `hashedEmail` (still present), and
 * SegmentIdentify re-identifies from it, restoring `ajs_user_id`.
 *
 * `window.analytics` is only set once `loadSegment` ran (write key present +
 * FSA/HSA) — the optional call makes the reset a silent no-op when Segment
 * never initialized (WDH, editor, blocked); `AnalyticsBrowser.reset` itself
 * is queue-safe pre-load.
 */
export function SegmentLogoutReset() {
  const navigation = useNavigation();
  const latch = useRef({ fired: false });

  useEffect(() => {
    const { fire, next } = computeLogoutResetTrigger(latch.current, {
      state: navigation.state,
      formMethod: navigation.formMethod,
      formAction: navigation.formAction,
    });
    latch.current = next;
    if (fire) void resetClientSegmentIdentity();
  }, [navigation.state, navigation.formMethod, navigation.formAction]);

  // BACKSTOP (defense-in-depth only — see computeIdentityDowngradeReset):
  // any FUTURE server-side teardown that reaches the loaders without a
  // client reset (fetcher paths missing the explicit forcedLogout signal)
  // degrades to a one-request lag instead of a persistent ajs_user_id leak.
  // The primary flows (submit-start latch above, handleForcedLogout) keep
  // their reset-before-identity-bearing-requests ordering — this never
  // substitutes for them.
  const { hashedEmail } = useRootLoaderData();
  const downgrade = useRef({ hadHashedEmail: Boolean(hashedEmail) });
  useEffect(() => {
    const { fire, next } = computeIdentityDowngradeReset(downgrade.current, {
      hashedEmail: hashedEmail ?? null,
      ajsUserIdPresent: Boolean(getCookieValue(document.cookie, "ajs_user_id")),
    });
    downgrade.current = next;
    if (fire) void resetClientSegmentIdentity();
  }, [hashedEmail]);

  return null;
}
